import { BodyTextL, BodyTextS, cs, useIsDesktop } from "@repo/ui";

import {
    type GetLifeDetailV2Response,
    PensionPhaseStatus,
    ProductType,
} from "@repo/api";
import { type FC } from "react";
import { type SupportedContractType, useReportError } from "@repo/logic";
import { useContractById, useLifeDetail, usePensionDetail } from "../../queryHooks";
import { type DataWithStatus, mapToDataWithStatus } from "../../types/global";
import { TransactionsListSkeleton } from "../DPS/Transactions/TransactionsList";
import { DebtCtaPay } from "../Link/CTA/PayById";
import { type DebtInformation, useDebtInformation } from "../Link/CTA/useDebtInformation";
import ContractCard, { ContractCardSkeletonHeader } from "./ContractCard";
import { ContractHeaderWithFufiData } from "./ContractCard/ContractCardHeader";
import { ContractContentStack, ContractErrorStack, ContractPaper } from "./Contract.styled";
import SmartActionsById from "./SmartAction";
import TransactionsById from "./Transaction";

type ContractProps = DataWithStatus<{
    contractId: string;
    type: SupportedContractType;
    hasSmartActions: boolean;
    debtInformation: DebtInformation | null;
    transactionsComponent: JSX.Element;
    headerComponent: JSX.Element;
}> & { isError?: boolean };

export const Contract: FC<ContractProps> = ({ data, isSuccess, isError }) => {
    const isDesktop = useIsDesktop();

    if (isError) {
        return (
            <ContractPaper isDesktop={isDesktop} elevation={isDesktop ? 4 : 0}>
                <ContractErrorStack spacing={3}>
                    {data && (
                        <BodyTextS mb={0}>
                            {cs.global.contractNumber} {data.contractId}
                        </BodyTextS>
                    )}
                    <BodyTextL>{cs.global.contractDetailUnavailable}</BodyTextL>
                </ContractErrorStack>
            </ContractPaper>
        );
    }

    const getTransactions = () => {
        if (!isSuccess) return <TransactionsListSkeleton skeletonsCount={4} />;
        return data?.transactionsComponent ?? <TransactionsListSkeleton skeletonsCount={4} />;
    };

    const getHeader = () => {
        if (!isDesktop) return null;
        if (!isSuccess) return <ContractCardSkeletonHeader />;
        return data?.headerComponent ?? <ContractCardSkeletonHeader />;
    };

    return (
        <ContractPaper isDesktop={isDesktop} elevation={isDesktop ? 4 : 0}>
            <ContractContentStack spacing={5}>
                {getHeader()}
                {Boolean(isSuccess && data?.hasSmartActions && data?.contractId && data?.type) && (
                    <SmartActionsById id={data!.contractId} type={data!.type} />
                )}
                {data?.debtInformation?.isSuccess && data.debtInformation.data && (
                    <DebtCtaPay debtStatus={data.debtInformation.data} />
                )}
                {getTransactions()}
            </ContractContentStack>
        </ContractPaper>
    );
};

type IContractWithData = {
    contractId: string;
    onDetailError?: () => void;
};
type IFufiContractWithData = IContractWithData & {
    proposalNumber?: string | null;
};

const DPSContractCard = ({ contractId, onDetailError }: IContractWithData) => {
    const { data, isSuccess } = useContractById({ id: contractId });
    const { isError } = usePensionDetail({ id: contractId, type: ProductType.Uf });
    const isPayout = isSuccess && data.pensionPhaseStatus === PensionPhaseStatus.Payout;
    const hasDetailError = isSuccess && isError && !isPayout;

    useReportError(hasDetailError, onDetailError);

    return (
        <Contract
            isError={hasDetailError}
            {...mapToDataWithStatus(
                {
                    contractId,
                    type: ProductType.Uf,
                    debtInformation: null,
                    hasSmartActions: !isSuccess || data.pensionPhaseStatus !== PensionPhaseStatus.Payout,
                    headerComponent: <ContractCard contractId={contractId} position="preview" type={ProductType.Uf} />,
                    transactionsComponent: <TransactionsById id={contractId} type={ProductType.Uf} />,
                },
                isSuccess,
            )}
        />
    );
};

const PPContractCard = ({ contractId, onDetailError }: IContractWithData) => {
    const { data, isSuccess } = useContractById({ id: contractId });
    const { isError } = usePensionDetail({ id: contractId, type: ProductType.Pf });
    const isPayout = isSuccess && data.pensionPhaseStatus === PensionPhaseStatus.Payout;
    const hasDetailError = isSuccess && isError && !isPayout;

    useReportError(hasDetailError, onDetailError);

    return (
        <Contract
            isError={hasDetailError}
            {...mapToDataWithStatus(
                {
                    contractId,
                    type: ProductType.Pf,
                    debtInformation: null,
                    hasSmartActions: !isSuccess || data.pensionPhaseStatus !== PensionPhaseStatus.Payout,
                    headerComponent: <ContractCard contractId={contractId} position="preview" type={ProductType.Pf} />,
                    transactionsComponent: <TransactionsById id={contractId} type={ProductType.Pf} />,
                },
                isSuccess,
            )}
        />
    );
};

const LifeContractCard = ({ contractId, onDetailError }: IContractWithData) => {
    const debtInformation = useDebtInformation();
    const { isError } = useLifeDetail({ id: contractId, contractType: ProductType.Cl });

    useReportError(isError, onDetailError);

    return (
        <Contract
            isSuccess={!isError}
            isError={isError}
            data={{
                contractId,
                type: ProductType.Cl,
                debtInformation,
                hasSmartActions: true,
                headerComponent: <ContractCard contractId={contractId} position="preview" type={ProductType.Cl} />,
                transactionsComponent: (
                    <TransactionsById
                        id={contractId}
                        type={ProductType.Cl}
                    />
                ),
            }}
        />
    );
};

const FufiContractCard = ({ contractId, proposalNumber, onDetailError }: IFufiContractWithData) => {
    const { data, isSuccess, isError } = useLifeDetail<GetLifeDetailV2Response>({
        id: contractId,
        proposalNumber,
        contractType: ProductType.Clf,
    });

    useReportError(isError, onDetailError);

    return (
        <Contract
            isSuccess={isSuccess}
            isError={isError}
            data={{
                contractId,
                type: ProductType.Clf,
                hasSmartActions: true,
                debtInformation: null,
                headerComponent: (
                    <ContractHeaderWithFufiData contractId={contractId} proposalNumber={data?.proposalNumber} />
                ),
                transactionsComponent: (
                    <TransactionsById
                        isLoading={!isSuccess}
                        id={contractId}
                        type={ProductType.Clf}
                    />
                ),
            }}
        />
    );
};

export const contractByIdFromType = {
    [ProductType.Uf]: DPSContractCard,
    [ProductType.Pf]: PPContractCard,
    [ProductType.Cl]: LifeContractCard,
    [ProductType.Clf]: FufiContractCard,
};
